import {
  Component,
  ElementRef,
  NgZone,
  OnInit,
  ViewChild,
} from "@angular/core";
import { LeftSideNavComponent } from "../../../shared/layout/app/left-menu/left-side-nav.component";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { TicketsBllService } from "../tickets-bll.service";
import stateDistrict from "../../../../assets/state_districts_data/state_districts.json";
import { Router, RouterLink } from "@angular/router";
import { HeaderComponent } from "../../../shared/layout/app/header/header.component";
import { MobileInputComponent } from "../../../shared/components/mobile-input/mobile-input.component";
import {
  AssignChildTicketRequest,
  AssignTicketIconModel,
  AssignTicketRequest,
  PHOTO_REQUIRED_TICKET_TYPES,
  TicketModel,
  TicketSuggestion,
  UserEventStatusResp,
} from "../tickets.model";
import {
  APIErrorModel,
  BaseResponse,
} from "../../../shared/models/base-response.model";
import { LoaderService } from "../../../shared/components/loader/loader.service";
import { DeviceTypeService } from "../../../core/services/device-type.service";
import { UserService } from "../../../core/services/user.service";
import { ProfileBllService } from "../../profile/profile-bll.service";
import { SupportButtonComponent } from "../../../shared/components/support-button/support-button.component";
import { environment } from "../../../../environments/environment";
import { HomeBllService } from "../../home/home-bll.service";
import { EventDetailsResponseModel } from "../../home/home.model";

declare var bootstrap: any;

@Component({
  selector: "app-assign-ticket",
  imports: [
    LeftSideNavComponent,
    ReactiveFormsModule,
    CommonModule,
    MobileInputComponent,
    HeaderComponent,
    RouterLink,
    SupportButtonComponent,
  ],
  templateUrl: "./assign-ticket.component.html",
  styleUrls: ["./assign-ticket.component.scss"],
})
export class AssignTicketComponent implements OnInit {
  // @ViewChild("PaymentSuccessModal") paymentSuccess!: ElementRef;
  @ViewChild("ConfirmationModal") confirmationModalRef!: ElementRef;
  @ViewChild("SuccessModal") successModal!: ElementRef;

  // Define the form group for the assign ticket form
  assignForms!: FormArray; // multiple forms

  /**
   * Modal flags
   */
  discount: string = ""; // default
  activeFormIndex: number | null = null;

  /**
   * Selected file for profile logo
   */
  previewLogos: string[] = []; // preview for each ticket
  selectedFiles: (File | null)[] = [];
  invalidImages: string[] = []; // error message per ticket
  private clearMsgTimeouts: any[] = [];

  allowedFileTypes: string[] = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/svg",
  ]; // Array of strings
  maxFileSize: number = 15 * 1024 * 1024; // Number (5 MB in bytes)
  logoTitle: string = ""; // String (title or name for the logo)

  /**
   * State and district data for dropdowns
   * This data is imported from a JSON file containing state and district mappings.
   */
  state_District: { [key: string]: string[] } = stateDistrict;
  states: string[] = Object.keys(this.state_District);
  cities: string[] = [];

  suggestions: TicketSuggestion[] = [];
  tickets: TicketModel[] = []; // fetched from API
  ticketCities: string[][] = []; // cities per ticket
  shownSuggestionKeys = new Set<string>(); // in-memory
  selectedMyselfIndex: number | null = null; // index of ticket where Myself is checked
  disableOtherMyself: boolean = false; // global flag to disable other checkboxes
  hideMyselfOption = false; // default: Myself visible

  selectedIndex!: number; // index of ticket for which confirmation modal is shown
  ticketCountryInfo: {
    mobile: string;
    countryCode: string;
    countryName: string;
  }[] = []; // store country info per ticket

  userProfile: UserEventStatusResp["user_profile"] | null = null; // fetched from API
  userEventStatus: UserEventStatusResp | null = null;
  error_messages: boolean[] = [];

  /**
   * Display icons and colors for ticket types
   */
  AssignTicketIcons: Record<
    string,
    { white: string; primary: string; secondary: string } | undefined
  > = AssignTicketIconModel;
  showMyTicketsLink: boolean = false;
  redirectOnClose: boolean = false;

  suggestionsLoading = false;
  isMobileDevice: boolean = false;

  imagePreview: string | ArrayBuffer | null = null;
  faceDetected: boolean[] = [];
  loading = false;

  // variable to hold the source of user(android_web/ios_web/web)
  source: string = "";
  hasError: boolean[] = [];

  supportPhone = environment.support_phone; // Support phone number

  eventDetails: EventDetailsResponseModel | null = null;
  showRegClosedMsg: boolean = false;

  constructor(
    private fb: FormBuilder,
    private ticketsBll: TicketsBllService,
    private router: Router, // Router to navigate between routes
    private zone: NgZone,
    private loader: LoaderService,
    private deviceService: DeviceTypeService,
    private userService: UserService,
    private profileBll: ProfileBllService,
    private bllService: HomeBllService
  ) {}

  async ngOnInit(): Promise<void> {
    // Initialize the form here if needed
    // Fetch discount from query params
    this.discount = this.ticketsBll.getDiscountAmount() ?? "";
    this.assignForms = this.fb.array([]);
    this.getSuggestionsList();
    this.fetchUserEventStatus();
    this.isMobileDevice = this.deviceService.isMobile();
    // await this.ticketsBll.loadModels();
    // Get the source of user
    this.source = this.deviceService.getUserSource();

    this.loadEventDetails();
  }

  /** Create a form group for each ticket */
  private createAssignForm(): FormGroup {
    const form = this.fb.group({
      profilePic: [""],
      firstName: new FormControl("", [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(60),
        this.ticketsBll.nameValidator(), // Custom validator for name
        this.ticketsBll.startsWithLetterValidator(), // Custom validator to check if starts with letter
      ]),

      lastName: new FormControl("", [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(60),
        this.ticketsBll.nameValidator(), // Custom validator for lastName
        this.ticketsBll.startsWithLetterValidator(), // Custom validator to check if starts with letter
      ]),
      mobile: new FormControl("", [
        Validators.required,
        Validators.pattern(/^[0-9]{6,12}$/), // Accepts 6 to 12 digits
      ]),
      state: ["", Validators.required],
      city: [{ value: "", disabled: true }], // initially disabled
      isHistory: [0],
      suggestionImageUrl: this.fb.nonNullable.control<string>(""), // store suggestion image URL if any
    });

    // Subscribe to state changes
    form.get("state")?.valueChanges.subscribe((state) => {
      const index = this.assignForms.controls.indexOf(form);

      if (state) {
        this.ticketCities[index] = this.state_District[state] || [];
        form.get("city")?.enable();
        form.get("city")?.setValue("");
        form.get("city")?.setValidators([Validators.required]); //  add validator
        form.get("city")?.updateValueAndValidity();
      } else {
        this.ticketCities[index] = [];
        form.get("city")?.disable();
        form.get("city")?.setValue("");
        form.get("city")?.clearValidators(); // clear validator
        form.get("city")?.updateValueAndValidity();
      }
    });

    // Reset error message dynamically when any input changes
    form.get("firstName")?.valueChanges.subscribe(() => {
      this.clearError(form);
    });

    form.get("lastName")?.valueChanges.subscribe(() => {
      this.clearError(form);
    });

    form.get("mobile")?.valueChanges.subscribe(() => {
      this.clearError(form);
    });

    return form;
  }

  private clearError(form: FormGroup) {
    const index = this.assignForms.controls.indexOf(form);
    if (index !== -1) {
      this.error_messages[index] = false;
    }
  }

  /** Reusable method to enable/disable city based on state */
  updateCityOptions(state: string, form: FormGroup) {
    const cities = this.state_District[state] || [];
    const cityControl = form.get("city");
    if (!cityControl) return;

    if (cities.length > 0) {
      cityControl.enable({ emitEvent: false });
    } else {
      cityControl.disable({ emitEvent: false });
    }
  }

  /**
   * Custom validator to check if the name contains only valid characters
   * @returns Validator function
   */
  restrictInvalidChars(event: KeyboardEvent): void {
    const key = event.key;

    const allowedSpecialChars = [".", "-", "'", "’"];
    const isLetter = /^[\p{L}]$/u.test(key); // Unicode letter
    const isAllowedSpecialChar = allowedSpecialChars.includes(key);
    const isSpace = key === " ";

    if (!isLetter && !isAllowedSpecialChar && !isSpace) {
      event.preventDefault(); // Block disallowed characters
    }
  }

  /**
   * Handle paste event to remove consecutive spaces
   * @param event ClipboardEvent
   * @param controlName Name of the form control
   * @param maxLength Maximum length for the input
   */
  onPasteRemoveConsecutiveSpaces(
    event: ClipboardEvent,
    controlName: string,
    maxLength: number,
    form: FormGroup
  ): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData("text") || "";
    const cleanedText = pastedText.trim().replace(/\s{2,}/g, " ");
    const input = event.target as HTMLInputElement;
    const currentValue = input.value;
    const selectionStart = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;

    const newValue =
      currentValue.slice(0, selectionStart) +
      cleanedText +
      currentValue.slice(selectionEnd);

    const truncatedValue = newValue.slice(0, maxLength);
    input.value = truncatedValue;

    // update the correct FormGroup
    const control = form.get(controlName);
    if (control) {
      control.setValue(truncatedValue);
    }

    const cursorPos = Math.min(selectionStart + cleanedText.length, maxLength);
    setTimeout(() => {
      input.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  }

  /**
   * Prevent leading space and consecutive spaces in input
   * @param event KeyboardEvent
   */
  preventLeadingSpace(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (event.key === " ") {
      const cursorPos = input.selectionStart || 0;

      // Prevent space at start
      if (cursorPos === 0) {
        event.preventDefault();
        return;
      }
      // Prevent consecutive space
      // Check character immediately before the cursor
      if (cursorPos > 0 && input.value.charAt(cursorPos - 1) === " ") {
        event.preventDefault();
      }
    }
  }

  /**
   * Enforce maximum length for input
   * @param event InputEvent
   * @param maxLength Maximum length for the input
   */
  enforceMaxLength(
    event: Event,
    maxLength: number,
    control: AbstractControl | null
  ): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if (value.length > maxLength) {
      value = value.substring(0, maxLength);
      input.value = value; // update DOM
    }

    // keep Angular FormControl in sync
    if (control) {
      control.setValue(value, { emitEvent: true });
      control.markAsDirty();
      control.updateValueAndValidity();
    }
  }

  /**
   * Handle profile logo selection
   * @param event Change event from file input
   */
  onProfileLogoSelected(event: Event, index: number): void {
    // Clear previous timeout if still running
    if (this.clearMsgTimeouts[index]) {
      clearTimeout(this.clearMsgTimeouts[index]);
      this.clearMsgTimeouts[index] = null;
    }

    this.invalidImages[index] = ""; // Reset error immediately
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    this.faceDetected[index] = false;
    const file = input.files[0];

    //  Validate file type
    if (!this.allowedFileTypes.includes(file.type)) {
      this.showInvalidImage(index, "Invalid file type.", input);
      return;
    }

    // Validate file size
    if (file.size > this.maxFileSize) {
      this.showInvalidImage(
        index,
        "Please select an image smaller than 15 MB.",
        input
      );
      return;
    }
    
    // Normalize extension to lowercase
    const fileName = file.name;
    const ext = fileName.split('.').pop()?.toLowerCase() ?? "";
    const base = fileName.substring(0, fileName.lastIndexOf(".")) || fileName;

    // build final lowercase extension name
    const normalizedName = `${base}.${ext}`;

    // Create a new File object with updated name
    const normalizedFile = new File([file], normalizedName, { type: file.type });

    // Store lowercase filename for UI or upload
    this.logoTitle = normalizedName;

    // Replace original file with normalized file
    this.selectedFiles[index] = normalizedFile;

    // FileReader for preview
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result as string;
      this.previewLogos[index] = base64;
      this.invalidImages[index] = "";
      this.hasError[index] = false;
    };
    reader.readAsDataURL(normalizedFile);

    // Update reactive form with normalized file 
    this.getForm(index).get("profilePic")?.setValue(normalizedFile);
    this.getForm(index).get("profilePic")?.updateValueAndValidity();
  }

  /** Helper to show invalid image message and reset input */
  private showInvalidImage(
    index: number,
    message: string,
    input: HTMLInputElement
  ): void {
    this.invalidImages[index] = message;
    this.selectedFiles[index] = null;
    this.previewLogos[index] = "/assets/images/Default-profile-photo.png";
    input.value = "";
    this.hasError[index] = true;

    this.clearMsgTimeouts[index] = setTimeout(() => {
      this.invalidImages[index] = "";
    }, 3000);
  }

  // Fetches history-based ticket suggestions and updates the component state.
  getSuggestionsList(): Promise<TicketSuggestion[]> {
    return this.ticketsBll
      .fetchTicketSuggestions()
      .then((response) => {
        const suggestions =
          response && response.data && Array.isArray(response.data)
            ? response.data
            : [];
        this.suggestions = suggestions;
        this.prepareSuggestions(suggestions); // prepare with cleaned URLs
        // Also return the list for callers that await/chain
        return suggestions;
      })
      .catch((err) => {
        return [];
      });
  }

  // * Shows a Bootstrap modal by element id.
  showBootstrapModal(id: string): void {
    const el = document.getElementById(id);
    if (!el) return;
    const modal = bootstrap.Modal.getOrCreateInstance(el, {
      backdrop: "static",
    });
    modal.show();
  }

  // Hide modal by ID
  hideBootstrapModal(id: string): void {
    const el = document.getElementById(id);
    if (!el) return;
    const modal = bootstrap.Modal.getOrCreateInstance(el, {
      backdrop: "static",
    });
    modal.hide();
  }

  /**
   * @returns void
   * Fetch the ticket list from the API and initialize forms
   */
  getTicketList() {
    this.loader.show();
    return this.ticketsBll
      .getTicketList(0)
      .then((result) => {
        this.tickets = result.data ?? [];

        // Sort tickets by id ascending
        this.tickets.sort((a, b) => {
          if (a.is_child === b.is_child) return 0; // same type, keep original order
          return a.is_child ? 1 : -1; // child goes to bottom
        });

        // Find the maximum display_index from non-child tickets
        const maxNonChildIndex = Math.max(
          0,
          ...this.tickets
            .filter((t) => !t.is_child)
            .map((t) => t.display_index ?? 0)
        );

        // Start counting from that max
        let childIndex = maxNonChildIndex;

        this.tickets.forEach((ticket) => {
          if (ticket.is_child) {
            ticket.display_index = ++childIndex;
          }
        });

        this.assignForms.clear(); // reset

        this.previewLogos = this.tickets.map(
          (_) => "/assets/images/Default-profile-photo.png"
        );
        this.selectedFiles = this.tickets.map((_) => null);
        this.invalidImages = this.tickets.map((_) => "");

        // create one form per ticket
        this.tickets.forEach((ticket, i) => {
          const form = this.createAssignForm();

          // populate city options
          if (this.userProfile?.state) {
            const cities = this.state_District[this.userProfile.state] || [];
            this.ticketCities.push(cities);
          }

          // If ticket is a child, prefill values and disable fields
          if (ticket.is_child) {
            form.get("city")?.enable();

            const mobileValue = this.userProfile?.mobile || "";
            const countryName = this.userProfile?.country || "India";
            const countryCode = this.userProfile?.country_code || "+91";

            form.patchValue({
              mobile: {
                mobile: mobileValue,
                countryName: countryName,
                countryCode: countryCode,
              },
              state: this.userProfile?.state || "",
              city: this.userProfile?.city || "",
            });

            // Disable editing
            form.get("mobile")?.disable({ emitEvent: false });
            form.get("state")?.disable({ emitEvent: false });
            form.get("city")?.disable({ emitEvent: false });

            // Store country info separately
            this.ticketCountryInfo[i] = {
              mobile: mobileValue,
              countryName: this.userProfile?.country || "",
              countryCode: this.userProfile?.country_code || "",
            };
          } else {
            form.patchValue({
              state: this.userProfile?.state || "",
              city: this.userProfile?.city || "",
            });
          }
          this.assignForms.push(form);
        });
        this.ticketsBll.callClearSummaryId();
        this.loader.hide();
      })
      .catch((error: APIErrorModel) => {
        this.loader.hide();
        if (error.status === 410) {
          this.router.navigateByUrl("home", { replaceUrl: true });
        } else if (error.status === 404) {
          this.router.navigateByUrl("reg/my-tickets", { replaceUrl: true });
        } else {
          alert(
            error.message || "Failed to fetch ticket list. Please try again."
          );
        }
      });
  }

  /** Access form by index */
  getForm(i: number): FormGroup {
    return this.assignForms.at(i) as FormGroup;
  }

  /** Fetch user profile and event status */
  fetchUserEventStatus() {
    this.loader.show();
    this.profileBll
      .submitGetUserEventStatus()
      .then((res: BaseResponse<UserEventStatusResp>) => {
        console.log("User event status fetched successfully:", res);
        if (res !== null) {
          this.userEventStatus = res.data;
          this.userProfile = res.data.user_profile;

          let previewLogo =
            this.userProfile.image_url ??
            "/assets/images/Default-profile-photo.png";
          let userName =
            res.data.user_profile.first_name +
            " " +
            res.data.user_profile.last_name;
          this.userService.setUser({
            name: userName, // get from API or JWT
            profileImg: previewLogo, // fallback if empty
            mobile: res.data.user_profile.mobile,
            state: res.data.user_profile.state,
            city: res.data.user_profile.city,
            hasUnassignedTickets:
              res.data.event_my_tickets.unassigned_tickets_bought_by_me > 0,
            hasAssignedTickets: res.data.event_my_tickets.assigned_tickets_bought_by_me > 0 || res.data.event_my_tickets.tickets_assigned_for_me > 0,
          });
          if (
            this.userProfile.mobile?.startsWith(this.userProfile.country_code)
          ) {
            this.userProfile.mobile = this.userProfile.mobile.slice(
              this.userProfile.country_code.length
            );
          }
          this.getTicketList();
        }
      })
      .catch((err: APIErrorModel) => {
        this.loader.hide();
        console.error("Error fetching user event status:", err);
        alert("Failed to fetch user event status. Please try again.");
      });
  }
  /**
   * Handle the "Myself" checkbox change event.
   * @param event Event
   * @param index number
   * @returns void
   */
  async onMyselfChecked(event: Event, index: number): Promise<void> {
    this.ticketsBll.saveUseSuggestion("0");

    const input = event.target as HTMLInputElement;
    // this.checkedMyself[index] = input.checked; // track state
    const form = this.getForm(index);

    if (!form) return; // safety check

    if (input.checked && this.userProfile) {
      this.selectedMyselfIndex = index;
      form.patchValue(
        {
          firstName: this.userProfile.first_name || "",
          lastName: this.userProfile.last_name || "",
          mobile: {
            mobile: this.userProfile.mobile || "",
            countryCode: this.userProfile.country_code || "+91",
            countryName: this.userProfile.country || "India",
          },
          state: this.userProfile.state || "",
          city: this.userProfile.city || "",
        },
        { emitEvent: true }
      );

      // set country info separately
      this.ticketCountryInfo[index] = {
        mobile: this.userProfile.mobile || "",
        countryName: this.userProfile.country || "India",
        countryCode: this.userProfile.country_code || "+91",
      };

      if (this.userProfile.image_url) {
        this.previewLogos[index] = this.userProfile.image_url;
        this.invalidImages[index] = "";
        this.hasError[index] = false;
      } else {
        this.previewLogos[index] = "/assets/images/Default-profile-photo.png";
      }

      // disable mobile input
      form.get("mobile")?.disable();

      if (this.userProfile.state) {
        this.ticketCities[index] =
          this.state_District[this.userProfile.state] || [];
        form.get("city")?.enable();
      } else {
        form.get("city")?.disable();
      }
    } else {
      // also unmark “shown once” for this row so it can show again
      const key = this.getFormKey(index);
      this.shownSuggestionKeys.delete(key);
      this.selectedMyselfIndex = null;
      // Clear values but keep validators intact
      form.patchValue(
        {
          firstName: "",
          lastName: "",
          mobile: {
            mobile: "",
            countryCode: "+91",
            countryName: "India",
          },
          state: this.userProfile?.state || "",
          city: this.userProfile?.city || "",
          image_url: "",
        },
        { emitEvent: true }
      );

      // Mark mobile control as untouched so error doesn't show
      form.get("firstName")?.markAsUntouched();
      form.get("firstName")?.markAsPristine(); // optional, resets dirty
      form.get("lastName")?.markAsUntouched();
      form.get("lastName")?.markAsPristine(); // optional, resets dirty
      form.get("mobile")?.markAsUntouched();
      form.get("mobile")?.markAsPristine(); // optional, resets dirty

      this.ticketCountryInfo[index] = {
        countryName: "India",
        countryCode: "+91",
        mobile: "",
      };
      // restore cities for the prefilled state
      if (this.userProfile?.state) {
        this.ticketCities[index] =
          this.state_District[this.userProfile.state] || [];
        form.get("city")?.enable({ emitEvent: false });
      } else {
        form.get("city")?.disable({ emitEvent: false });
      }

      // re-enable mobile input + dropdown
      form.get("mobile")?.enable({ emitEvent: false });
      this.previewLogos[index] = "/assets/images/Default-profile-photo.png";
    }
  }

  fallbackImg = "assets/images/default-profile-individual.png"; // use one style; avoid leading '/'

  /** Call this right after fetching suggestions */
  prepareSuggestions(raw: any[] = []): void {
    this.suggestions = raw.map((s) => ({
      ...s,
      _img: this.cleanImageUrl(s?.image_url) || this.fallbackImg,
    }));
  }

  /** Angular-safe image error handler */
  onListImgError(ev: Event, s: any): void {
    const img = ev.target as HTMLImageElement;
    if (!img) return;
    img.onerror = null; // prevent loops if fallback fails
    img.src = this.fallbackImg; // show fallback in UI
    if (s) s._img = this.fallbackImg; // keep data in sync
  }

  // Open the suggestions modal for a specific ticket
  openSuggestionsFor(i: number): void {
    // if "My Self" is selected on this row, don't open suggestions
    if (this.selectedMyselfIndex === i) return;
    if (!this.suggestions || this.suggestions.length === 0) return;
    // if child ticket, don't show suggestions
    if (this.tickets?.[i]?.is_child) return;

    // 1) capture the previous row BEFORE changing activeFormIndex
    const prevIndex = this.activeFormIndex;
    const prevKey = prevIndex != null ? this.getFormKey(prevIndex) : null;

    // 2) current key
    const currKey = this.getFormKey(i);

    // 3) if switching rows, unmark the previous row
    if (prevKey && prevKey !== currKey) {
      this.shownSuggestionKeys.delete(prevKey);
    }

    // 4) set current as active
    this.activeFormIndex = i;

    // 5) if current row not yet shown (or was unmarked), mark & open
    if (!this.shownSuggestionKeys.has(currKey)) {
      this.shownSuggestionKeys.add(currKey);
      this.showBootstrapModal("SuggestionsModal");
    }
    // else: already shown for this row → do nothing (keeps your "show once per row" rule)
  }

  // Use the selected suggestion to populate the form
  async useSuggestion(s: any, idx?: number): Promise<void> {
    const i = (idx ?? this.activeFormIndex)!;
    if (i === null || i === undefined) return;

    const fg = this.getForm(i); // existing getter returning FormGroup

    const normalized = this.normalizeMobile(s?.mobile || "");
    // Clean image URL safely
    const cleanedImage = this.cleanImageUrl(s?.image_url);
    const fallbackImg = "/assets/images/Default-profile-photo.png";
    const finalImg = cleanedImage || fallbackImg;

    this.previewLogos[i] = finalImg;
    this.invalidImages[i] = "";
    this.hasError[i] = false;
    fg.get("suggestionImageUrl")!.setValue(finalImg || "", {
        emitEvent: false,
      });

    // 1) Names & mobile
    fg.patchValue(
      {
        firstName: (s.first_name || "").trim(),
        lastName: (s.last_name || "").trim(),
        mobile: normalized,

        //image_url: s.image_url || "/assets/images/Default-profile-photo.png",
      },
      { emitEvent: true }
    );

    // set country info separately
    this.ticketCountryInfo[i] = {
      mobile: this.normalizeMobile(s.mobile) || "",
      countryName: "India",
      countryCode: "+91",
    };

    this.ticketsBll.saveUseSuggestion("1");

    // 2) State/City (your selects bind to strings, so set strings)
    const stateName = s.state || "";
    const cityName = s.city || "";

    if (stateName) {
      fg.get("state")?.setValue(stateName, { emitEvent: true });
      if (cityName && this.ticketCities[i]?.includes(cityName)) {
        fg.get("city")?.setValue(cityName, { emitEvent: true });
      } else {
        fg.get("city")?.reset("");
      }
    } else {
      fg.get("state")?.reset("");
      fg.get("city")?.reset("");
      this.ticketCities[i] = [];
    }
    fg.markAllAsTouched(); //show validation instantly

    this.hideBootstrapModal("SuggestionsModal"); //Close the suggestions modal
  }

  formatMobile(m?: string): string {
    return this.normalizeMobile1(m || "");
  }

  // Normalize mobile number by removing country code and spaces
  normalizeMobile(m?: string): string {
    if (!m) return "";
    return m.replace(/^\+91\s?/, "").replace(/\s+/g, "");
  }
  normalizeMobile1(m?: string): string {
    if (!m) return "";

    // 1) Remove a leading "+91" (with or without space)
    // 2) Remove all non-digits
    let digits = String(m)
      .replace(/^\s+|\s+$/g, "")
      .replace(/^\+?91\s?/, "")
      .replace(/\D+/g, "");

    // If fewer than 6 digits, just return as-is (no space to insert yet)
    if (digits.length <= 5) return digits;

    // 3) Insert a space after the 5th digit
    return digits.slice(0, 5) + " " + digits.slice(5);
  }

  //Returns a stable, string key for a given ticket form row.
  private getFormKey(i: number): string {
    const t = this.tickets?.[i] as any;
    return String(t?.id ?? t?.ticket_id ?? `idx-${i}`);
  }

  onMobileChange(
    event: { mobile: string; countryCode: string; countryName: string },
    index: number
  ) {
    // form already has mobile via ControlValueAccessor
    // store country details separately
    this.ticketCountryInfo[index] = {
      mobile: event.mobile,
      countryCode: event.countryCode,
      countryName: event.countryName,
    };
  }

  /**
   * Open the confirmation modal for the specified ticket.
   * @param i Index of the ticket
   */
  openConfirmationModal(i: number): void {
    this.selectedIndex = i;
    const modalEl = this.confirmationModalRef.nativeElement;
    const modal = new bootstrap.Modal(modalEl);
    modal.show();
  }

  /** Hide the confirmation modal */
  hideConfirmationModal(): void {
    const modalEl = this.confirmationModalRef.nativeElement;
    const modal = bootstrap.Modal.getInstance(modalEl);
    modal?.hide();
  }

  /** Show success modal */
  AssignTicketSuccessModal(redirectOnClose: boolean = false) {
    this.showMyTicketsLink = true;
    const modalEl = this.successModal.nativeElement;
    // Reset the GIF to replay it
    const img: HTMLImageElement = modalEl.querySelector("img");
    if (img) {
      const newImg = img.cloneNode(true) as HTMLImageElement;
      const src = img.src.split("?")[0];
      newImg.src = `${src}?t=${Date.now()}`; // force reload

      img.replaceWith(newImg);
    }

    // Show the modal
    const modal = new bootstrap.Modal(modalEl);
    modal.show();

    setTimeout(() => {
      modal.hide();

      //redirect if flag is passed
      if (redirectOnClose) {
        this.router.navigateByUrl("reg/my-tickets?assigned=true", {
          replaceUrl: true,
        });
      }
    }, 5000);

    // store flag so Next button knows what to do
    this.redirectOnClose = redirectOnClose;
  }

  /** Submit for specific ticket */
  onSubmit(i: number) {
    this.loader.show();
    const form = this.getForm(i);
    if (form.valid) {
      const isHistory =
        this.ticketsBll.getUseSuggestion() === "1" ? "1" : form.value.isHistory;
      const suggestionUrl = (
        form.get("suggestionImageUrl")!.value || ""
      ).trim();

      console.log(`Form ${i} submitted:`, form.value);
      const countryInfo = this.ticketCountryInfo[i] || {
        mobile: "",
        countryCode: "",
        countryName: "",
      };

      // Determine payload based on ticket type
      let payload: AssignTicketRequest | AssignChildTicketRequest;

      if (this.tickets[i].is_child) {
        payload = {
          fname: form.value.firstName?.trim(),
          lname: form.value.lastName?.trim(),
          file_name_with_extension: "",
        };
      } else {
        payload = {
          fname: form.value.firstName?.trim(),
          lname: form.value.lastName?.trim(),
          country: countryInfo.countryName,
          country_code: countryInfo.countryCode,
          mobile: countryInfo.countryCode + countryInfo.mobile,
          is_history: isHistory,
          assign_source: this.source,
          language: this.profileBll.getSelectedLanguage(),
          state: form.value.state,
          city: form.value.city,
          ...(isHistory === "1" && suggestionUrl
            ? { suggestion_image_url: suggestionUrl }
            : {}),
        };
      }

      // Append file only if selected
      if (this.selectedFiles[i]) {
        payload.file_name_with_extension = this.logoTitle;
        this.callgetImageUploadLinkAPI(
          this.selectedFiles[i],
          payload,
          this.tickets[i].encoded_ticket_code,
          i
        );
      } else {
        this.callAssignTicket(payload, this.tickets[i].encoded_ticket_code, i);
      }
    }
  }

  /**
   * Call the API to get the image upload link
   */
  callgetImageUploadLinkAPI(
    selectedFile: File,
    payload: any,
    encodedTicketCode: string,
    i: number
  ) {
    this.ticketsBll
      .getImageUploadLinkChallenge(selectedFile.name, encodedTicketCode)
      .then((response) =>
        this.handleUploadImageLinkStatusSuccess(
          response,
          selectedFile,
          payload,
          encodedTicketCode,
          i
        )
      )
      .catch((err: APIErrorModel) =>
        this.handleUploadImageLinkStatusError(err)
      );
  }

  /**
   * Handle the successful response for the image upload link.
   * @param response The API response containing the upload link.
   * @param selectedFile The file to be uploaded.
   * @param payload The request payload.
   * @param encodedTicketCode The encoded ticket code.
   */
  handleUploadImageLinkStatusSuccess(
    response: any,
    selectedFile: File,
    payload: any,
    encodedTicketCode: string,
    i: number
  ) {
    console.log("Received upload link:", response);
    this.ticketsBll
      .uploadImageToS3(
        response.data.s3_media_post.url,
        response.data.s3_media_post.fields,
        selectedFile
      )
      .then((res) => {
        // After successful upload, call assign ticket API
        // Extract file_name_with_extension from response
        if (response.data?.file_name_with_extension) {
          payload.file_name_with_extension =
            response.data.file_name_with_extension;
        }
        this.callAssignTicket(payload, encodedTicketCode, i);
      })
      .catch((err) => {
        this.loader.hide();
        console.error("Error uploading image:", err);
      });
  }

  /**
   * Handle the error response for the image upload link.
   * @param err The error object from the API call.
   */
  handleUploadImageLinkStatusError(err: APIErrorModel): void {
    this.loader.hide();
    console.error("Error fetching image upload link:", err);
  }

  /**
   * Assign a ticket to a user.
   * @param payload The request payload.
   * @param encodedTicketCode The encoded ticket code.
   */
  callAssignTicket(
    payload: AssignTicketRequest | AssignChildTicketRequest,
    encodedTicketCode: string,
    i: number
  ) {
    this.ticketsBll
      .assignTicket(payload, encodedTicketCode)
      .then(async (message) => {
        await this.refreshSuggestions(); // 👈 wait for fresh data
        this.ticketsBll.resetSuggestionFlag();
        this.loader.hide();
        this.hideConfirmationModal();

        // If this was assigned to myself, hide option permanently
        if (this.selectedMyselfIndex !== null) {
          const index = this.selectedMyselfIndex;
          this.hideMyselfOption = true;
          this.selectedMyselfIndex = null;

          const form = this.getForm(index);
          if (form) {
            form.get("mobile")?.enable({ emitEvent: true });
            form.get("mobile")?.updateValueAndValidity();
          }
        }

        // remove assigned row & reset index
        this.tickets.splice(i, 1);
        this.ticketCities.splice(i, 1);
        this.assignForms.removeAt(i);
        this.previewLogos.splice(i, 1);
        this.selectedFiles.splice(i, 1);
        this.invalidImages.splice(i, 1);
        this.error_messages.splice(i, 1);
        this.activeFormIndex = null;

        if (this.tickets.length === 0) {
          this.AssignTicketSuccessModal(true);
        } else {
          this.AssignTicketSuccessModal();
        }
      })
      .catch((err: APIErrorModel) => {
        this.ticketsBll.resetSuggestionFlag();
        this.loader.hide();
        this.hideConfirmationModal();
        if (err.status === 409) {
          this.error_messages[i] = true;
          setTimeout(() => {
            this.error_messages[i] = false;
          }, 15000); // 15000 ms = 15 seconds
        } else {
          alert("Failed to assign ticket. Please try again.");
        }
      });
  }

  /** Normalize weird image_url values:
   * - trims spaces
   * - removes wrapping single/double quotes
   * - unescapes \" from JSON-escaped strings
   * - collapses duplicate slashes in the path part (keeps https:// intact)
   */
  cleanImageUrl(u?: string): string {
    if (!u) return "";

    let s = String(u).trim();

    // Remove wrapping quotes if present:  "\"https://...\""  or  '"https://..."'
    s = s.replace(/^"(.*)"$/, "$1").replace(/^'(.*)'$/, "$1");

    // Unescape \" -> "
    s = s.replace(/\\"/g, '"');

    // If someone gave https:////domain.com... normalize protocol-level slashes to exactly 2
    s = s.replace(/^(https?:)\/{2,}/i, "$1//");

    // Collapse duplicate slashes ONLY in the path (keep protocol + host as-is)
    const m = s.match(/^(https?:\/\/[^\/]+)(\/.*)$/i);
    if (m) {
      const base = m[1];
      const path = m[2].replace(/\/{2,}/g, "/"); // // -> /
      s = base + path;
    }

    return s;
  }

  // Simple sleep function to simulate delay
  private sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

  // Refresh the suggestions list (handles array | single object | nested shapes)
  async refreshSuggestions(): Promise<void> {
    this.suggestionsLoading = true;
    try {
      await this.sleep(2000);

      const res = await this.ticketsBll.fetchTicketSuggestions(); // returns BaseResponse<TicketSuggestionResponse>

      // normalize: data.data | data | body → always an array
      let raw: any = res?.data?.data ?? res?.data ?? res;
      if (typeof raw === "string") {
        try {
          raw = JSON.parse(raw);
        } catch {}
      }
      const items = Array.isArray(raw)
        ? raw
        : raw && typeof raw === "object"
        ? [raw]
        : [];

      this.zone.run(() => {
        this.suggestions = [...items]; // replace reference so *ngFor updates
        this.shownSuggestionKeys.clear(); // optional: reset any gating
        this.suggestionsLoading = false;
        (this as any).cd?.markForCheck?.(); // if you injected ChangeDetectorRef; safe-optional
      });
    } catch {
      this.zone.run(() => {
        this.suggestions = [];
        this.suggestionsLoading = false;
        (this as any).cd?.markForCheck?.();
      });
    }
  }

  /**
   * Check if the assign button should be disabled for a ticket form.
   * @param i Index of the ticket/form
   * @returns True if the button should be disabled, false otherwise
   */
  isAssignDisabled(i: number): boolean {
    const ticket = this.tickets[i];
    const form = this.getForm(i);

    // Check if profile image is missing or default
    const img = this.previewLogos[i];
    const isDefaultImage =
      !img ||
      img === "/assets/images/Default-profile-photo.png" ||
      img === "assets/images/default-profile-individual.png";

    // Check if photo is required for this ticket
    const needsPhoto = this.requiresProfilePhoto(ticket);
    // Final condition: disable if form invalid, or (photo required and invalid image)
    return !form.valid || (needsPhoto && (this.hasError[i] || isDefaultImage));
  }

  /**
   * Returns true if the ticket requires a profile photo.
   * @param ticket The ticket to check
   */
  requiresProfilePhoto(ticket: TicketModel): boolean {
    return (
      ticket.is_child ||
      PHOTO_REQUIRED_TICKET_TYPES.has(ticket.ticket_type_code)
    );
  }

  /** TrackBy function for ngFor */
  trackByTicketId(index: number, ticket: any): any {
    return ticket.id || ticket.ticket_code || index;
  }

  onNextClick(): void {
    const modalEl = this.successModal.nativeElement;
    const modal = bootstrap.Modal.getInstance(modalEl);
    modal?.hide();

    if (this.redirectOnClose) {
      this.router.navigateByUrl("reg/my-tickets?assigned=true", {
        replaceUrl: true,
      });
    }
  }

  loadEventDetails() {
    this.bllService.fetchEventDetails()
      .then((response) => {
        this.eventDetails = response.data;     
        this.userService.setLeftMenu({
          allow_to_register: this.eventDetails.allow_to_register,
          show_digital_catalog: this.eventDetails.show_digital_catalog,
          event_status: this.eventDetails.event_status
        }); 
        if (this.eventDetails?.allow_to_register !== 1){
          this.showRegClosedMsg = true;
        } else {
          this.showRegClosedMsg = false;
        }      
      })
      .catch((error) => {
        this.showRegClosedMsg = false;
        console.error('Error fetching event details:', error);
      });
  }

}
